import { Ref, computed, readonly } from 'vue';

import { storeToRefs } from 'pinia';

import { useRoute } from 'vue-router/composables';
import { useQuery } from '@/helpers/composables/useApollo';

import {
	GetArticlesTimelineDocument,
	GetArticlesTimelineQuery,
	GetArticlesTimelineQueryVariables,
} from '@/features/editorial/graphql/queries/GetArticleTimeline.query';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { BackdropProfile, ImageFormat } from '@/@types/graphql-types';
import { getHeaderImage } from '@/features/editorial/helpers/editorial-helper';
import { useTimelinePageScroll } from './useTimelinePageScroll';

export function useArticleTimeline(el: Ref<HTMLElement | null>) {
	const route = useRoute();

	const { language, country, locale } = storeToRefs(useLanguageStore());

	const queryVariables = computed<GetArticlesTimelineQueryVariables>(() => {
		return {
			input: {
				cursor: 1,
				first: 3,
				locale: locale.value,
			},
			country: country.value,
			language: language.value,
			profile: BackdropProfile.S640,
			format: ImageFormat.Webp,
		};
	});

	const enabled = computed(() => route.name === 'app.editorial.subdomain.home');

	const { result, loading, fetchMore, onError } = useQuery<GetArticlesTimelineQuery>(
		GetArticlesTimelineDocument,
		queryVariables,
		{
			errorPolicy: 'all',
			enabled,
		}
	);

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetArticleTimeline]:',
			{
				error: error,
				where: 'Component: useArticleTimeline.ts',
			},
			'error'
		);
	});

	const articleTimeline = computed(() =>
		result.value?.getArticlesTimeline.articleGroups.map(bucket => ({
			...bucket,
			articles: bucket.articles.map(article => ({ ...article, headerImage: getHeaderImage(article) })),
		}))
	);

	const nextCursor = computed(() => result.value?.getArticlesTimeline.nextCursor ?? 0);
	const hasMore = computed(() => nextCursor.value > 0);

	const { pageBoundaryBuckets } = useTimelinePageScroll(el, () => ({
		loading,
		result,
		hasMore,
		fetchMore: fetchMoreArticles,
	}));

	async function fetchMoreArticles() {
		if (!hasMore.value) return false;

		const bucketsBefore = articleTimeline.value?.length ?? 0;

		await fetchMore({
			variables: {
				input: {
					...queryVariables.value.input,
					cursor: nextCursor.value,
				},
			},
			updateQuery: (previousResult, { fetchMoreResult }) => {
				if (!fetchMoreResult) return previousResult;

				return {
					...fetchMoreResult,
					getArticlesTimeline: {
						...fetchMoreResult.getArticlesTimeline,
						articleGroups: [
							...previousResult.getArticlesTimeline.articleGroups,
							...fetchMoreResult.getArticlesTimeline.articleGroups,
						],
					},
				};
			},
		});

		pageBoundaryBuckets.value = [...pageBoundaryBuckets.value, bucketsBefore];

		return true;
	}

	return {
		articleTimeline,
		loading,
		hasMore,
		fetchMoreArticles,
		pageBoundaryBuckets: readonly(pageBoundaryBuckets),
	};
}
